import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faFlask, faSpinner } from '@fortawesome/free-solid-svg-icons';
import examApi from '../../services/api/examApi';
import './LabTable.css';

/**
 * Component to display lab data for a question in a collapsible table.
 * 
 * Features:
 * - Automatically fetches lab data for each question using HATEOAS details link
 * - Falls back to direct API call if HATEOAS link is not available
 * - Auto-expands when lab data is found
 * - Handles 404 errors gracefully (when no lab data exists for a question)
 * - Resets state when question changes to ensure lab data is fetched for every question
 */
const LabTable = ({ 
  currentQuestion, 
  themeStyles, 
  isDarkMode 
}) => {
  const [labData, setLabData] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasCheckedForLab, setHasCheckedForLab] = useState(false);
  
  // Fetch lab data when question changes or when user expands the section
  const fetchLabData = useCallback(async () => {
    if (!currentQuestion?.id) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // console.log(`[LabTable] Fetching lab data for question ${currentQuestion.id}`);
      // console.log(`[LabTable] Question HATEOAS links available:`, currentQuestion._links);
      
      // Try using the HATEOAS details link first if available
      let labDetails = null;
      if (currentQuestion._links?.details?.href) {
        // console.log(`[LabTable] Using HATEOAS details link: ${currentQuestion._links.details.href}`);
        labDetails = await examApi.followHateoasLink(currentQuestion._links.details.href);
      } else {
        // Fall back to direct API call
        const detailsUrl = `/api/question/${currentQuestion.id}/details`;
        // console.log(`[LabTable] No HATEOAS details link found, using direct API call: ${detailsUrl}`);
        // console.log(`[LabTable] Current question object:`, JSON.stringify(currentQuestion, null, 2));
        labDetails = await examApi.followHateoasLink(detailsUrl);
      }
      
      // Filter for lab data (type: "Lab")
      const labItems = Array.isArray(labDetails) 
        ? labDetails.filter(detail => detail.type === 'Lab')
        : [];
      
      // console.log(`[LabTable] Found ${labItems.length} lab items for question ${currentQuestion.id}:`, labItems);
      
      setLabData(labItems);
      setHasCheckedForLab(true);
      
      // Auto-expand if lab data is found
      if (labItems.length > 0) {
        setIsExpanded(true);
        // console.log(`[LabTable] Auto-expanding lab section for question ${currentQuestion.id} with ${labItems.length} lab items`);
      }
      
    } catch (err) {
      // console.log(`[LabTable] Error fetching lab data for question ${currentQuestion.id}:`, err);
      
      // Check if this is a 404 error (no lab data exists) vs a real error
      const is404 = err.message?.includes('404') || err.status === 404 || err.response?.status === 404;
      
      if (is404) {
        // console.log(`[LabTable] No lab data found for question ${currentQuestion.id} (404 - normal behavior)`);
        setError(null); // Don't show error for missing lab data
      } else {
        console.error(`[LabTable] Real error fetching lab data for question ${currentQuestion.id}:`, err);
        setError('Failed to load lab data');
      }
      
      setLabData([]);
      setHasCheckedForLab(true);
    } finally {
      setIsLoading(false);
    }
  }, [currentQuestion]);

  // Reset lab check state when question changes
  useEffect(() => {
    setHasCheckedForLab(false);
    setLabData([]);
    setError(null);
    setIsExpanded(false); // Reset expansion state for new question
  }, [currentQuestion?.id]);

  useEffect(() => {
    if (currentQuestion?.id && (isExpanded || !hasCheckedForLab)) {
      fetchLabData();
    }
  }, [currentQuestion?.id, isExpanded, fetchLabData, hasCheckedForLab]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Don't render anything if there's no lab data and we've already checked
  if (!isLoading && hasCheckedForLab && labData.length === 0) {
    return null;
  }

  // Don't render anything if no question is loaded
  if (!currentQuestion?.id) {
    return null;
  }

  return (
    <div className="lab-table-container">
      {/* Header - always visible */}
      <div 
        className={`lab-header ${isDarkMode ? 'dark' : 'light'} ${isExpanded ? 'expanded' : ''}`}
        onClick={toggleExpanded}
      >
        <div className="lab-title">
          <FontAwesomeIcon icon={faFlask} className="lab-icon" />
          <span>Lab Results</span>
          {isLoading && (
            <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#3b82f6', fontSize: '14px' }} />
          )}
          {!isLoading && hasCheckedForLab && labData.length > 0 && (
            <span className={`lab-count-badge ${isDarkMode ? 'dark' : 'light'}`}>
              {labData.length}
            </span>
          )}
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {isLoading && (
            <div className={`lab-loading ${isDarkMode ? 'dark' : 'light'}`}>
              <span>Loading...</span>
            </div>
          )}
          <FontAwesomeIcon 
            icon={isExpanded ? faChevronUp : faChevronDown} 
            className="lab-icon"
            style={{ fontSize: '14px' }}
          />
        </div>
      </div>

      {/* Table - only visible when expanded */}
      {isExpanded && (
        <>
          {error && (
            <div className={`lab-error ${isDarkMode ? 'dark' : 'light'}`}>
              {error}
            </div>
          )}
          
          {!error && !isLoading && labData.length > 0 && (
            <table className={`lab-table ${isDarkMode ? 'dark' : 'light'}`}>
              <thead>
                <tr>
                  <th>Test</th>
                  <th>Value</th>
                  <th>Reference</th>
                </tr>
              </thead>
              <tbody>
                {labData.map((item, index) => (
                  <tr key={item.id || index}>
                    <td>
                      <strong>{item.name || 'N/A'}</strong>
                    </td>
                    <td>
                      {item.value || 'N/A'}
                    </td>
                    <td>
                      {item.reference || 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          
          {!error && !isLoading && labData.length === 0 && hasCheckedForLab && (
            <div className={`lab-no-data ${isDarkMode ? 'dark' : 'light'}`}>
              No lab data available for this question.
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LabTable;
